import React, { useState } from 'react';
import { PolicyRevision } from '../../types';
import { GitCompare, Calendar, ChevronDown, ChevronUp, FileCode, CheckCircle2 } from 'lucide-react';

interface TimelineNodeProps {
  revision: PolicyRevision;
  previousRevision?: PolicyRevision;
  isFirst: boolean;
  isLast: boolean;
  onViewDiff: (baseId: string, targetId: string) => void;
  onReadDocument?: (revision: PolicyRevision) => void;
}

export const TimelineNode: React.FC<TimelineNodeProps> = ({
  revision,
  previousRevision,
  isFirst,
  isLast,
  onViewDiff,
  onReadDocument,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const formatDate = (isoString?: string | null) => {
    if (!isoString) return 'Undated';
    return new Date(isoString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const analysis = revision.diffAnalysis || revision.summary;
  const score = analysis?.significanceScore ?? analysis?.significance_score ?? 0;
  const sigLevel = analysis?.significance;
  const isBaseline = !previousRevision || score === 0 || (sigLevel && sigLevel.toUpperCase().includes('BASELINE'));

  // Timeline node dot color
  const getNodeColor = () => {
    if (isBaseline) return 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950';
    return 'border-indigo-600 bg-white dark:bg-slate-800 shadow-indigo-200 dark:shadow-indigo-950';
  };

  const wordCount = revision.wordCount || revision.word_count || 3500;
  const keyChanges = analysis?.keyChanges || analysis?.key_changes || [];
  const headline = analysis?.headline || analysis?.change_headline;
  const structDiff = revision.structuredDiff || revision.structured_diff;
  const stats = structDiff?.stats || revision.diffSummary || revision.diff_summary;

  return (
    <div className="relative flex items-start gap-4 group">
      {/* Visual Dot on Rail */}
      <div
        className={`w-6 h-6 rounded-full border-4 ${getNodeColor()} z-10 flex-shrink-0 mt-3 shadow-md group-hover:scale-110 transition-transform`}
      />

      {/* Revision Card Content */}
      <div className="flex-1 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700/80 shadow-2xs p-5 hover:border-slate-300 dark:hover:border-slate-600 transition-all">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2.5">
            <span className="font-bold text-slate-900 dark:text-white text-base">
              {revision.title || `Revision ${revision.id}`}
            </span>
            {isBaseline && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-indigo-50 dark:bg-indigo-950/70 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800">
                Baseline
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              Effective: {formatDate(revision.effectiveDate || revision.effective_date || revision.scrapedAt || revision.scraped_at)}
            </span>
            <span>•</span>
            <span>{wordCount.toLocaleString()} words</span>
          </div>
        </div>

        {/* Change Headline */}
        {headline ? (
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200 mb-3 bg-slate-50 dark:bg-slate-700/50 p-2.5 rounded-lg border-l-4 border-indigo-500">
            {headline}
          </p>
        ) : isBaseline ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 italic mb-3">
            Initial baseline snapshot recorded. Subsequent revisions will calculate comparative diffs against this version.
          </p>
        ) : null}

        {/* Key Changes Bullets (Collapsible) */}
        {keyChanges.length > 0 && (
          <div className="space-y-1.5 mb-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-xs font-semibold text-slate-500 hover:text-slate-700 dark:text-slate-400 flex items-center gap-1"
            >
              Key Takeaways ({keyChanges.length})
              {isExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            </button>
            {isExpanded && (
              <ul className="space-y-1 text-xs text-slate-600 dark:text-slate-300 pl-4 list-disc">
                {keyChanges.map((item, idx) => (
                  <li key={idx} className="leading-relaxed">
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Action Controls & Line Diff Stats */}
        <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-100 dark:border-slate-700/80">
          <div className="flex items-center gap-3 text-xs">
            {stats ? (
              <div className="flex items-center gap-2 font-mono">
                <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                  +{stats.linesAdded || stats.lines_added || 0}
                </span>
                <span className="text-rose-600 dark:text-rose-400 font-semibold">
                  -{stats.linesRemoved || stats.lines_removed || 0}
                </span>
                <span className="text-slate-400">
                  ({stats.linesModified || stats.lines_modified || 0} modified)
                </span>
              </div>
            ) : isBaseline ? (
              <span className="text-slate-400 italic">Baseline document</span>
            ) : null}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => onReadDocument?.(revision)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 transition-colors shadow-2xs"
            >
              <FileCode className="w-3.5 h-3.5" />
              Read Document
            </button>

            {!isBaseline && previousRevision && (
              <button
                onClick={() => onViewDiff(previousRevision.id, revision.id)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors shadow-2xs"
              >
                <GitCompare className="w-3.5 h-3.5" />
                View Visual Diff
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const TimelineCard = TimelineNode;
export default TimelineNode;
